import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/** Return true to leave the open feedback item; false to stay. */
export type TryLeaveUserFeedback = () => Promise<boolean>;

type UserFeedbackLeaveGuardContextValue = {
  registerTryLeave: (fn: TryLeaveUserFeedback | null) => void;
  tryLeave: () => Promise<boolean>;
};

const UserFeedbackLeaveGuardContext =
  createContext<UserFeedbackLeaveGuardContextValue | null>(null);

export function UserFeedbackLeaveGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const tryLeaveRef = useRef<TryLeaveUserFeedback | null>(null);

  const registerTryLeave = useCallback((fn: TryLeaveUserFeedback | null) => {
    tryLeaveRef.current = fn;
  }, []);

  const tryLeave = useCallback(async (): Promise<boolean> => {
    if (!tryLeaveRef.current) {
      return true;
    }
    return tryLeaveRef.current();
  }, []);

  const value = useMemo(
    () => ({ registerTryLeave, tryLeave }),
    [registerTryLeave, tryLeave],
  );

  return (
    <UserFeedbackLeaveGuardContext.Provider value={value}>
      {children}
    </UserFeedbackLeaveGuardContext.Provider>
  );
}

export function useUserFeedbackLeaveGuard(): UserFeedbackLeaveGuardContextValue | null {
  return useContext(UserFeedbackLeaveGuardContext);
}

/** Register while an open feedback item is mounted; cleared on unmount. */
export function useRegisterUserFeedbackLeaveGuard(
  tryLeave: TryLeaveUserFeedback | null,
): void {
  const guard = useUserFeedbackLeaveGuard();

  useEffect(() => {
    if (!guard) {
      return;
    }
    guard.registerTryLeave(tryLeave);
    return () => guard.registerTryLeave(null);
  }, [guard, tryLeave]);
}

/** True when navigation leaves the open feedback item for a different item or another page. */
export function isLeavingUserFeedbackDetail(
  fromPath: string,
  toPath: string,
): boolean {
  const fromId = feedbackDetailId(fromPath);
  if (!fromId) {
    return false;
  }
  return feedbackDetailId(toPath) !== fromId;
}

function feedbackDetailId(path: string): string | null {
  const [route, query = ''] = path.split('?');
  if (route !== '/user-feedback') return null;
  const match = /(?:^|&)id=(\d+)/.exec(query);
  return match ? match[1] : null;
}
